import React from 'react';

import SpendAdjustmentActions from '../Actions';

import { useChildComponent } from '@gogame/hooks-ts';
import { useChooseFiles } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import {
  useScreenTitle,
  useScreenGo,
  useScreenDispatch,
  useScreenState,
  ScreenLink,
} from '@gogame/screens';

import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import {
  SpendAdjustmentsFormEdit,
  SpendAdjustmentsFormCopy,
  SpendAdjustmentsFormCreate,
} from 'scenes/SpendAdjustments/components/SpendAdjustmentForm';
import {
  COLUMN_ID,
  COLUMN_TYPE,
  COLUMN_AFFILIATE,
  COLUMN_APPLICATION,
  COLUMN_APPROVERS,
  COLUMN_CAMPAIGNS,
  COLUMN_NETWORK,
  COLUMN_PLATFORM,
  COLUMN_CLUSTER,
  COLUMN_AMOUNT,
  COLUMN_ORIGINAL_AMOUNT,
  COLUMN_CURRENCY,
  COLUMN_STATUS,
  COLUMN_PERIOD,
  COLUMN_REASON,
  COLUMN_UPDATED_DATE,
  COLUMN_CREATED_DATE,
  COLUMN_CREATED_BY,
} from 'scenes/SpendAdjustments/constants';
import {
  useLoadSAGrid,
  useIsUpdating,
  useImportSA,
  useImportSAByCampaigns,
  useExportGrid,
} from 'scenes/SpendAdjustments/data';
import {
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
  SET_TABLE_SETTINGS,
} from 'scenes/SpendAdjustments/screenReducer';

import { getColor } from '../Status';

import SpendAdjustmentToolbar from './Toolbar';
import { spendAdjustmentDetailsLink } from '../../navigation';

const SpendAdjustmentDetailsLink = ({ value }) => {
  return (
    <ScreenLink
      link={spendAdjustmentDetailsLink}
      pathParams={{
        id: value,
      }}
    >
      {value}
    </ScreenLink>
  );
};

const SpendAdjustmentsDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const [childComponent, openChildComponent] = useChildComponent();
  const go = useScreenGo();
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();

  const chooseFiles = useChooseFiles({ multiple: false, accept: '.csv' });

  const [importSATemplate] = useImportSA();
  const [importSAByCampaigns] = useImportSAByCampaigns();
  const [exportGrid] = useExportGrid();

  const isUpdating = useIsUpdating();

  const { data, isLoading, isRefetching } = useLoadSAGrid({
    filters: screenState.filters,
    searchQuery: screenState.searchQuery,
    settings: screenState.tableSettings,
  });

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleEdit = ({ id }) => {
    openChildComponent(SpendAdjustmentsFormEdit, {
      id,
    });
  };

  const handleCopy = ({ id }) => {
    return new Promise((resolve) => {
      openChildComponent(SpendAdjustmentsFormCopy, {
        id,
        onSubmit: resolve,
      });
    });
  };
  const handleOpenAffiliate = (_, affiliate) => {
    openChildComponent(AffiliateDetails, affiliate);
  };

  const handleCreate = () => {
    openChildComponent(SpendAdjustmentsFormCreate, {
      onSubmit: (response) => {
        go(response.id);
      },
    });
  };

  const handleImportSpendAdjustments = async () => {
    const file = await chooseFiles();
    const fileInFormData = new FormData();
    fileInFormData.append(
      'file',
      new Blob([file], { type: 'application/octet-stream' }),
      file.name
    );
    await importSATemplate(fileInFormData);
  };
  const handleImportSpendAdjustmentsByCampaigns = async () => {
    const file = await chooseFiles();
    const fileInFormData = new FormData();
    fileInFormData.append(
      'file',
      new Blob([file], { type: 'application/octet-stream' }),
      file.name
    );
    await importSAByCampaigns(fileInFormData);
  };

  const handleExport = async () => {
    await exportGrid({
      filters: screenState.filters,
      searchQuery: screenState.searchQuery,
      settings: screenState.tableSettings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching || isUpdating}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={
          <SpendAdjustmentToolbar
            onImportSpendAdjustments={handleImportSpendAdjustments}
            onCreate={handleCreate}
            onExport={handleExport}
            onImportSpendAdjustmentsByCampaigns={
              handleImportSpendAdjustmentsByCampaigns
            }
          />
        }
        storageKey={'spendAdjustments'}
        ready={screenState.ready}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'link'}
          headerName={'ID'}
          linkComponent={SpendAdjustmentDetailsLink}
          width={70}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_PERIOD}
          type={'dateRange'}
          headerName={'Period'}
          width={188}
          getValue={(v) => [v.from, v.to]}
        />
        <DataGrid.Column
          field={COLUMN_AFFILIATE}
          headerName={'Affiliate'}
          width={240}
          type={'link'}
          onLinkClick={handleOpenAffiliate}
          getTooltip={(v) => v?.name}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Status'}
          type={'status'}
          width={132}
          getStatusColor={getColor}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_APPLICATION}
          headerName={'Application'}
          width={170}
          type={'text'}
          getValue={(v) => v?.name}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_NETWORK}
          headerName={'Network'}
          width={150}
          type={'text'}
          getValue={(v) => v?.name}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_CLUSTER}
          headerName={'Cluster'}
          width={150}
          type={'text'}
          getValue={(v) => v?.name}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_PLATFORM}
          headerName={'Platform'}
          width={150}
          type={'text'}
          getValue={(v) => v?.name}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_CAMPAIGNS}
          headerName={'Campaigns'}
          width={350}
          type={'commaList'}
          sortable={false}
        />

        <DataGrid.Column
          field={COLUMN_ORIGINAL_AMOUNT}
          headerName={'Original Amount'}
          width={92}
          type={'currency'}
          precision={2}
        />
        <DataGrid.Column
          field={COLUMN_AMOUNT}
          headerName={'Amount USD'}
          width={92}
          type={'currency'}
          precision={2}
        />

        <DataGrid.Column
          field={COLUMN_CURRENCY}
          headerName={'Currency'}
          width={72}
          type={'text'}
          sortable={false}
          formatter={(v) => v?.toUpperCase()}
        />

        <DataGrid.Column
          field={COLUMN_TYPE}
          headerName={'Type'}
          width={140}
          type={'text'}
          getValue={(v) => v?.name}
        />

        <DataGrid.Column
          field={COLUMN_REASON}
          headerName={'Reason'}
          width={140}
          type={'text'}
          sortable={false}
          getValue={(v) => v?.name}
        />

        <DataGrid.Column
          field={COLUMN_APPROVERS}
          headerName={'Approvers'}
          width={160}
          type={'commaList'}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED_DATE}
          headerName={'Last Updated'}
          width={160}
          type={'date'}
          template={'LLL'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          width={160}
          type={'text'}
          getValue={(v) => v?.name}
        />

        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          width={160}
          type={'date'}
          template={'LLL'}
        />

        <DataGrid.Column
          actionsColDef
          type={'actions'}
          onCopy={handleCopy}
          onEdit={handleEdit}
          limit={3}
          actionsComponent={SpendAdjustmentActions}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default SpendAdjustmentsDataTable;
